import api from '../api/axios';
import { StaleReason } from '../models/reportStaleness';

interface GenerateReportRequest {
  childId: number;
  startDate: string;
  endDate: string;
  reportDate?: string;  // display label — defaults to endDate on the server if omitted
  showHeader?: boolean;
  pdfPassword?: string; // if set, the generated PDF will be password-protected
  /** Report Builder template: a data.report_template id, or 'stock' for the
   *  code-defined stock Weekly Report. Omitted = legacy behaviour. */
  templateId?: number | 'stock';
  /** Generation-panel values for the template's manual-editable header fields. */
  fieldValues?: Record<string, string>;
}

interface ReportResponse {
  url: string;
  s3Key: string;
  startDate?: string;
  endDate?: string;
  reportDate?: string;
  createdAt?: string;
  createdByName?: string;
  templateId?: number | null;
  templateName?: string;
  /** Requested template no longer exists — the stock default was used instead. */
  templateMissing?: boolean;
  isPasswordProtected?: boolean;
  password?: string;
  /** Generation-panel entries used to render the PDF — hydrates the Build
   *  Report inputs so regenerating doesn't mean re-typing them. */
  fieldValues?: Record<string, string>;
  /** Why this stored report no longer matches its sources. Server-computed, so
   *  the press bay agrees with the History pill about the same report. */
  staleReasons?: StaleReason[];
}

export interface ReportListItem {
  id: number;
  uuid: string;
  childId: number;
  startDate: string;     // YYYY-MM-DD
  endDate: string;       // YYYY-MM-DD
  reportDate: string;    // YYYY-MM-DD
  createdAt: string;     // ISO timestamp
  createdByName: string;
  /** null = stock template (or the template was deleted after generation). */
  templateId: number | null;
  /** Stamped at generation — survives template rename/delete. */
  templateName: string;
  isPasswordProtected: boolean;
  isStale: boolean;
  /** Why the report no longer matches its sources, most consequential first.
   *  Empty when up to date. See models/reportStaleness.ts for the copy. */
  staleReasons: StaleReason[];
}

interface DownloadResponse {
  url: string;
  password?: string;
  isPasswordProtected: boolean;
}

export class ReportDataService {
  static async getExistingReport(
    childId: number,
    startDate: string,
    endDate: string,
    /** Narrows to one template identity ('stock' = the code default). Omitted = any template. */
    templateId?: number | 'stock'
  ): Promise<ReportResponse> {
    const qs = new URLSearchParams({ childId: String(childId), startDate, endDate });
    if (templateId !== undefined) qs.set('templateId', String(templateId));
    const response = await api.get(`/reports?${qs.toString()}`);
    return response.data;
  }

  static async generateReport(request: GenerateReportRequest): Promise<ReportResponse> {
    const response = await api.post('/reports', request);
    return response.data;
  }

  static async listReports(params: { startDate: string; endDate: string; childId?: number | null }): Promise<ReportListItem[]> {
    const qs = new URLSearchParams({
      startDate: params.startDate,
      endDate: params.endDate,
    });
    if (params.childId != null) qs.set('childId', String(params.childId));
    const response = await api.get(`/reports/list?${qs.toString()}`);
    return response.data.reports as ReportListItem[];
  }

  static async getReportDownload(id: number): Promise<DownloadResponse> {
    const response = await api.get(`/reports/${id}/download`);
    return response.data;
  }
}
